import React from 'react';
import PropTypes from 'prop-types';
import {
  createTheme, IconButton, InputAdornment, SvgIcon, TextField, ThemeProvider, useTheme
} from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { X as CancelIcon } from 'react-feather';
import { DATE_FORMAT } from 'src/utils/constants';
import theme from 'src/theme';
import { TRANSACTION_PICKER_THEME_OPTIONS } from 'src/components/transactions/transactionStyleTokens';

function DateFilterField({
  label = 'Filter by Date',
  value,
  onDateChange,
  hideClose = false,
  onlyDate = true,
  onClearFilter,
  pickerProps,
  pickerPopperSx,
  shrinkLabel = false,
  sx,
}) {
  const baseTheme = useTheme();
  const pickerTheme = React.useMemo(() => (
    pickerPopperSx
      ? createTheme(baseTheme, TRANSACTION_PICKER_THEME_OPTIONS)
      : baseTheme
  ), [baseTheme, pickerPopperSx]);
  const PickerComponent = onlyDate ? DatePicker : DateTimePicker;
  const displayFormat = onlyDate ? DATE_FORMAT.date : DATE_FORMAT.dateTime;
  const { renderInput: customRenderInput, ...pickerOptions } = pickerProps || {};
  const handlePickerChange = (date) => {
    if (!date || !date.isValid()) return;
    onDateChange?.(date);
  };
  const defaultRenderInput = (params) => (
    <TextField
      {...params}
      size="small"
      InputLabelProps={{
        ...params.InputLabelProps,
        ...(shrinkLabel ? { shrink: true } : {})
      }}
      sx={[
        {
          minWidth: { xs: '100%', sm: 180 },
          width: { xs: '100%', sm: 'auto' },
          '& .MuiOutlinedInput-root': {
            height: 40,
            borderRadius: 2,
            backgroundColor: '#F9FAFB',
            '& fieldset': {
              borderColor: '#E5E7EB'
            },
            '&:hover fieldset': {
              borderColor: '#D1D5DB'
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main
            }
          }
        },
        sx
      ]}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <InputAdornment position="end" sx={{ gap: 0.5 }}>
            {params.InputProps.endAdornment?.props?.children}
            {value && !hideClose && (
              <IconButton
                onClick={onClearFilter}
                size="small"
                sx={{
                  p: 0.5,
                  borderRadius: 2,
                  background: theme.palette.grey[100],
                  '&:hover': {
                    background: theme.palette.error.light,
                    color: theme.palette.common.white,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <SvgIcon fontSize="small">
                  <CancelIcon />
                </SvgIcon>
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ThemeProvider theme={pickerTheme}>
        <PickerComponent
          label={label}
          value={value}
          inputFormat={displayFormat}
          disableFuture
          onChange={handlePickerChange}
          {...pickerOptions}
          PopperProps={{
            sx: {
              '& .MuiPaper-root': {
                background: '#fff',
                borderRadius: 6,
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)', // Shadow
                border: '1px solid #0d0121ff', // Border
                ...pickerPopperSx?.paper
              },
              '& .MuiPickersDay-root': {
                borderRadius: '50%',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0b6e99, #2563eb)',
                  color: '#fff',
                  transform: 'scale(1.1)',
                },
                ...pickerPopperSx?.day,
              },
              '& .Mui-selected': {
                background: 'linear-gradient(45deg, #2563eb, #0b6e99) !important',
                color: '#fff',
                ...pickerPopperSx?.selected,
              },
              '& .MuiPickersCalendarHeader-root': {
                background: 'linear-gradient(45deg, #0b6e99, #2563eb)',
                color: '#fff',
                borderRadius: '8px 8px 0 0',
                ...pickerPopperSx?.calendarHeader,
              },
              ...pickerPopperSx?.popper,
            },
          }}
          renderInput={customRenderInput || defaultRenderInput}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
}

DateFilterField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  onlyDate: PropTypes.bool,
  hideClose: PropTypes.bool,
  onDateChange: PropTypes.func,
  onClearFilter: PropTypes.func,
  pickerProps: PropTypes.object,
  pickerPopperSx: PropTypes.shape({
    primaryColor: PropTypes.string,
    paper: PropTypes.object,
    day: PropTypes.object,
    selected: PropTypes.object,
    calendarHeader: PropTypes.object,
    popper: PropTypes.object
  }),
  shrinkLabel: PropTypes.bool,
  sx: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.func
  ]),
};

export default DateFilterField;
